import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './context/AuthContext';

import Landing from './pages/Landing';
import Subscribe from './pages/Subscribe';
import LegalPage from './pages/legal/LegalPage';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

import DashboardLayout from './pages/dashboard/DashboardLayout';
import Dashboard from './pages/dashboard/Dashboard';
import Spenders from './pages/dashboard/Spenders';
import BotConfig from './pages/dashboard/BotConfig';
import CrmBot from './pages/dashboard/CrmBot';
import Inbox from './pages/dashboard/Inbox';
import Accounts from './pages/dashboard/Accounts';
import DropFans from './pages/dashboard/DropFans';
import AccountTracking from './pages/dashboard/AccountTracking';
import TrackingLinks from './pages/dashboard/TrackingLinks';
import Stories from './pages/dashboard/Stories';
import Lists from './pages/dashboard/Lists';
import Scripts from './pages/dashboard/Scripts';
import ScriptsAnalytics from './pages/dashboard/ScriptsAnalytics';
import Vault from './pages/dashboard/Vault';
import MassMessages from './pages/dashboard/MassMessages';
import MessageLogs from './pages/dashboard/MessageLogs';
import BannedWords from './pages/dashboard/BannedWords';
import Chatters from './pages/dashboard/Chatters';
import EmployeeReport from './pages/dashboard/EmployeeReport';
import WeeklyReports from './pages/dashboard/WeeklyReports';
import Training from './pages/dashboard/Training';
import Whales from './pages/dashboard/Whales';
import MyStats from './pages/dashboard/MyStats';
import Accounting from './pages/dashboard/Accounting';
import Planning from './pages/dashboard/Planning';
import ManagePlanning from './pages/dashboard/ManagePlanning';
import Profile from './pages/dashboard/Profile';
import MyAgency from './pages/dashboard/MyAgency';
import Affiliation from './pages/dashboard/Affiliation';
import Subscription from './pages/dashboard/Subscription';
import Roles from './pages/dashboard/Roles';
import AdminDashboard from './pages/admin/AdminDashboard';
import { DEMO } from './lib/demo';
import DemoAccentSwitcher from './components/DemoAccentSwitcher';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080A] flex items-center justify-center" data-testid="auth-loading">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth/login" replace />;
  return children;
}

// Page ouverte à l'une OU l'autre des permissions ; sinon retour à l'accueil du tableau de bord (qui renvoie vers
// la première page permise). Bot d'accueil (E2, 14/09) : Owner / Co-owner (manage_telegram) et managers (msg_manage_boxes).
function RequireAnyPerm({ perms, children }) {
  const { hasPerm } = useAuth();
  return perms.some((p) => hasPerm(p)) ? children : <Navigate to="/dashboard" replace />;
}

// The dashboard home routes each user to the FIRST page their role can access — never
// to a page they lack permission for (landing a tracking-only employee on Messaging,
// which they can't view, is what triggered the "Something went wrong" crash).
// 30/09 (Marlon : « quand j'ouvre l'appli, ca m'affiche directement dans la messagerie, alors que
// j'aimerais que ca m'affiche d'abord le tableau de bord ») — L'ARRIVEE A FROID.
// Verifie avant d'ecrire ceci : la connexion envoie deja sur /dashboard, le manifeste demarre sur
// /dashboard, et l'appli ne memorise AUCUNE derniere page. Ce qui le ramenait dans la messagerie,
// c'est son navigateur qui restaure l'onglet ferme sur /dashboard/messages (ou un raccourci pointant
// dessus). On corrige donc au seul endroit ou on peut : au CHARGEMENT de la page.
//
// La regle est etroite exprès : on ne redirige que si TOUT est vrai —
//   1. c'est un vrai chargement de page, pas une navigation dans l'appli (`PREMIER_CHEMIN`, fige a
//      l'import du module : une fois l'appli demarree, il ne change plus) ;
//   2. aucune conversation n'est ciblee dans l'URL. Les liens « Ouvrir la conv » portent `conv=`,
//      donc ils continuent d'ouvrir la bonne conversation, y compris depuis un mail ou Discord ;
//   3. la personne a bien un tableau de bord a voir (une chatteuse n'en a pas : pour elle la
//      messagerie EST sa page d'accueil, on ne lui change rien).
// Et ca n'arrive qu'UNE fois par chargement (`dejaRedirige`) : cliquer ensuite sur « Messagerie »
// dans le menu marche normalement.
const PREMIER_CHEMIN = typeof window !== 'undefined' ? window.location.pathname : '';
const PREMIERE_RECHERCHE = typeof window !== 'undefined' ? window.location.search : '';
let dejaRedirige = false;
function MessagerieOuAccueil() {
  const { hasPerm } = useAuth();
  const aUnTableauDeBord = hasPerm('manage_telegram') || hasPerm('sales_view');
  const cibleUneConv = /[?&]conv=/.test(PREMIERE_RECHERCHE);
  if (!dejaRedirige && aUnTableauDeBord && !cibleUneConv
      && PREMIER_CHEMIN.replace(/\/+$/, '') === '/dashboard/messages') {
    dejaRedirige = true;
    return <Navigate to="/dashboard" replace />;
  }
  return <Inbox />;
}

function DashboardIndex() {
  const { hasPerm } = useAuth();
  if (hasPerm('manage_telegram') || hasPerm('sales_view')) return <Dashboard />;
  const dest =
    hasPerm('msg_read') ? '/dashboard/messages'
      : hasPerm('tracking_view') ? '/dashboard/tracking-links'
        : hasPerm('view_account_tracking') ? '/dashboard/account-tracking'
          : hasPerm('lists_view') ? '/dashboard/lists'
            : hasPerm('scripts_view') ? '/dashboard/scripts'
              : hasPerm('vault_view') ? '/dashboard/vault'
                : hasPerm('mass_view') ? '/dashboard/mass-messages'
                  : '/dashboard/profile';  // everyone can at least see their own profile
  return <Navigate to={dest} replace />;
}

// Capture affiliate referral code (?ref=CODE) on any entry page, so it
// survives navigation until the visitor reaches the register form.
try {
  const refCode = new URLSearchParams(window.location.search).get('ref');
  if (refCode) localStorage.setItem('chathub_ref', refCode.trim().toUpperCase());
} catch { /* storage unavailable (private mode) — non-blocking */ }

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={DEMO ? <Navigate to="/dashboard" replace /> : <Landing />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/legal/terms" element={<LegalPage docKey="terms" />} />
            <Route path="/legal/privacy" element={<LegalPage docKey="privacy" />} />
            <Route path="/legal/cookies" element={<LegalPage docKey="cookies" />} />
            <Route path="/legal/legal-notice" element={<LegalPage docKey="notice" />} />
            <Route path="/legal/dpa" element={<LegalPage docKey="dpa" />} />
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/register" element={<Register />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset" element={<ResetPassword />} />

            <Route path="/dashboard" element={<RequireAuth><DashboardLayout /></RequireAuth>}>
              <Route index element={<DashboardIndex />} />
              <Route path="accounts" element={<Accounts />} />
              <Route path="bot" element={<BotConfig />} />
              <Route path="crm-bot" element={<RequireAnyPerm perms={['manage_telegram', 'msg_manage_boxes']}><CrmBot /></RequireAnyPerm>} />
              <Route path="spenders" element={<Spenders />} />
              <Route path="messages" element={<MessagerieOuAccueil />} />
              <Route path="dropfans" element={<DropFans />} />
              <Route path="account-tracking" element={<AccountTracking />} />
              <Route path="tracking-links" element={<TrackingLinks />} />
              <Route path="stories" element={<Stories />} />
              <Route path="lists" element={<Lists />} />
              <Route path="scripts" element={<Scripts />} />
              <Route path="scripts/analytics" element={<ScriptsAnalytics />} />
              <Route path="vault" element={<Vault />} />
              <Route path="mass-messages" element={<MassMessages />} />
              <Route path="messages-log" element={<MessageLogs />} />
              <Route path="banned-words" element={<BannedWords />} />
              <Route path="my-stats" element={<MyStats />} />
              <Route path="employees" element={<Chatters />} />
              <Route path="employees/report" element={<EmployeeReport />} />
              <Route path="employees/weekly-reports" element={<WeeklyReports />} />
              <Route path="training" element={<Training />} />
              <Route path="whales" element={<Whales />} />
              <Route path="employees/accounting" element={<Accounting />} />
              <Route path="employees/planning" element={<Planning />} />
              <Route path="employees/planning/manage" element={<ManagePlanning />} />
              <Route path="profile" element={<Profile />} />
              <Route path="organizations/my-organization" element={<MyAgency />} />
              <Route path="affiliation" element={<Affiliation />} />
              <Route path="subscriptions" element={<Subscription />} />
              <Route path="billing" element={<Subscription />} />
              <Route path="organizations/roles" element={<Roles />} />
            </Route>

            <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
